import { toast } from 'react-toastify';
import { t } from 'i18next';
import { useQuery, useLazyQuery, useMutation } from '@apollo/client';

import { AccountNav } from '../Account/AccountNav';
import { useAuth } from '../../context/Auth';
import {
  IMembershipBillingAddress,
  IMembershipPaymentSources,
  IMembershipInvoices,
  IMembershipInvoiceUrl,
} from '../../types/model';

import {
  GET_MEMBERSHIP_BILLING_ADDRESS,
  LIST_MEMBERSHIP_PAYMENT_SOURCES,
  LIST_MEMBERSHIP_INVOICES,
  GET_MEMBERSHIP_INVOICE_URL,
  GENERATE_PORTAL_SESSION,
} from '../../http/apollo-queries';

import { Button, Card, Table } from '../../components/UI';
import { CustomNumber } from '../../components/CustomNumber/CustomNumber';

import { formatDate } from '../../helpers/functions';
import { Chargebee, PortalSession } from './types';

const Payments = () => {
  const { user } = useAuth();

  const chargebee = window['Chargebee'] as Chargebee;

  const cbInstance = chargebee.init({
    site: process.env.REACT_APP_CHARGEBEE_SITE,
    publishableKey: process.env.REACT_APP_CHARGEBEE_KEY,
  });

  const cbPortal = cbInstance.createChargebeePortal();

  const {
    data: { getMembershipBillingAddress: membershipBillingAddress } = {},
    loading: loadingMembershipBillingAddress,
  } = useQuery<{
    getMembershipBillingAddress: IMembershipBillingAddress;
  }>(GET_MEMBERSHIP_BILLING_ADDRESS);

  const {
    data: {
      listMembershipPaymentSources: {
        items: membershipPaymentSources = [],
      } = {},
    } = {},
    loading: loadingMembershipPaymentSources,
  } = useQuery<{
    listMembershipPaymentSources: { items: IMembershipPaymentSources[] };
  }>(LIST_MEMBERSHIP_PAYMENT_SOURCES);

  const {
    data: {
      listMembershipInvoices: { items: membershipInvoices = [] } = {},
    } = {},
    loading: loadingMembershipInvoices,
  } = useQuery<{
    listMembershipInvoices: { items: IMembershipInvoices[] };
  }>(LIST_MEMBERSHIP_INVOICES);

  const [getMembershipInvoiceUrl, { loading: loadingMembershipInvoiceUrl }] =
    useLazyQuery<{
      getMembershipInvoiceUrl: IMembershipInvoiceUrl;
    }>(GET_MEMBERSHIP_INVOICE_URL);

  const [generatePortalSession] = useMutation<{
    generatePortalSession: PortalSession;
  }>(GENERATE_PORTAL_SESSION);

  const handleDownloadInvoice = (id: string) => {
    getMembershipInvoiceUrl({ variables: { invoiceId: id } })
      .then(({ data }) => {
        const url = data?.getMembershipInvoiceUrl.downloadUrl;
        if (url) {
          window.open(url);
        } else {
          toast.warn(t('payments.invoiceNotAvailable'));
        }
      })
      .catch(() => {
        toast.error(t('payments.errorDownloadInvoice'));
      });
  };

  const handleChangePayment = () => {
    cbInstance.setPortalSession(() => {
      return new Promise((resolve) => {
        generatePortalSession().then(({ data }) => {
          return resolve(data?.generatePortalSession);
        });
      });
    });

    const callbacks = {};
    cbPortal.open(callbacks, {
      sectionType: chargebee.getPortalSections().PAYMENT_SOURCES,
    });
  };

  return (
    <div className="flex flex-col w-full bg-white text-black items-center dark:bg-black-real dark:text-white">
      <div className="w-full lg:w-2/3X">
        <AccountNav />

        <div className="flex flex-col">
          <>
            <Card
              className="mt-4"
              title={t('payments.paymentMethods')}
              padding={false}
            >
              <Table
                columns={[
                  t('payments.type'),
                  t('payments.maskedNumber'),
                  t('payments.expire'),
                  t('payments.renewal'),
                ]}
                data={membershipPaymentSources.map((m) => {
                  return [
                    `${m.object}/${m.brand}`,
                    m.maskedNumber,
                    `${m.expiryMonth}/${m.expiryYear}`,
                    t('payments.renewal_' + user?.membership.renewal),
                  ];
                })}
                isLoading={loadingMembershipPaymentSources}
                actions={['Edit']}
                onActionClick={() => {
                  handleChangePayment();
                }}
                emptyTableMessage={t('payments.noPaymentMethod')}
              />
            </Card>
            <Card
              className="mt-4"
              title={t('payments.billingAddress')}
              padding={false}
            >
              <div className={'inline-grid grid-cols-1 w-full'}>
                <Table
                  columns={[t('payments.address')]}
                  data={
                    membershipBillingAddress && [
                      [
                        `${membershipBillingAddress.firstName} 
                           ${membershipBillingAddress.lastName} - 
                           ${membershipBillingAddress.line1}
                           ${membershipBillingAddress.line2 || ''} - 
                           ${membershipBillingAddress.city || ''} - 
                           ${membershipBillingAddress.state || ''} - 
                           ${membershipBillingAddress.zip || ''} - 
                           ${membershipBillingAddress.country || ''}
                        `,
                      ],
                    ]
                  }
                  isLoading={loadingMembershipBillingAddress}
                  emptyTableMessage={t('payments.noBillingAddress')}
                />
              </div>
            </Card>
            <Card
              className="mt-4"
              padding={false}
              title={t('payments.invoiceHistory')}
            >
              <Table
                columns={[
                  t('payments.id'),
                  t('payments.status'),
                  t('payments.date'),
                  t('payments.total'),
                  t('payments.membershipPlan'),
                  '',
                ]}
                data={membershipInvoices.map((m) => {
                  return [
                    m.id,
                    m.status,
                    formatDate(m.date),
                    <CustomNumber value={m.total} suffix={'$'} />,
                    m.membershipPlan.title,
                    <Button
                      buttonClass={'mr-4'}
                      variant={'secondary'}
                      loading={loadingMembershipInvoiceUrl}
                      onClick={() => {
                        handleDownloadInvoice(m.id);
                      }}
                    >
                      {t('payments.downloadInvoice')}
                    </Button>,
                  ];
                })}
                isLoading={loadingMembershipInvoices}
                emptyTableMessage={t('payments.noMembershipInvoices')}
              />
            </Card>
          </>
        </div>
      </div>
    </div>
  );
};

export default Payments;
